import { AlertTriangle, CheckCircle2, Home as HomeIcon, Phone, ShieldCheck, Umbrella } from "lucide-react";
import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { IMAGES, SITE } from "@/data/site";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

function Checklist({ items, testId }) {
  return (
    <ul className="space-y-2.5" data-testid={testId}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#0F4C5C]" aria-hidden="true" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function Hurricane() {
  const { t } = useLang();
  usePageMeta(t("meta.hurricane")[0], t("meta.hurricane")[1]);
  return (
    <>
      <PageHero
        overline={t("hurricanePage.heroOverline")}
        title={t("hurricanePage.heroTitle")}
        sub={t("hurricanePage.heroSub")}
        testId="hurricane"
      />

      <section className="border-b border-[#0F4C5C]/10 bg-[#EEF6FC] py-8" data-testid="season-banner">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-x-10 gap-y-3 px-4 text-center sm:px-6 lg:px-8">
          <p className="flex items-center gap-2 font-heading text-lg font-bold text-[#0F4C5C]">
            <Umbrella className="h-5 w-5 text-[#D97706]" aria-hidden="true" /> {t("hurricanePage.seasonLabel")}
          </p>
          <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-slate-600">
            <AlertTriangle className="h-4 w-4 text-[#D97706]" aria-hidden="true" /> {t("hurricanePage.peak")}
          </p>
        </div>
      </section>

      <section className="bg-white pt-14 sm:pt-16" data-testid="hurricane-images-section">
        <div className="mx-auto grid max-w-7xl gap-4 px-4 sm:grid-cols-2 sm:px-6 lg:px-8">
          <Reveal>
            <img
              src={IMAGES.hurricaneDamage}
              alt={t("hurricanePage.damageAlt1")}
              className="h-64 w-full object-cover shadow-lg sm:h-80"
              loading="lazy"
              data-testid="hurricane-damage-image"
            />
          </Reveal>
          <Reveal delay={1}>
            <img
              src={IMAGES.hurricaneTrees}
              alt={t("hurricanePage.damageAlt2")}
              className="h-64 w-full object-cover shadow-lg sm:h-80"
              loading="lazy"
              data-testid="hurricane-trees-image"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-16 pt-10 sm:pb-24 sm:pt-14" aria-label="Preparedness checklists" data-testid="hurricane-checklists">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3">
            <Reveal>
              <article className="flex h-full flex-col border-t-4 border-[#0F4C5C] bg-[#F8FAFC] p-7" data-testid="before-storm-card">
                <h2 className="font-heading text-xl font-bold text-slate-900">{t("hurricanePage.beforeTitle")}</h2>
                <h3 className="mt-5 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#0F4C5C]">
                  <HomeIcon className="h-4 w-4" aria-hidden="true" /> {t("hurricanePage.secureTitle")}
                </h3>
                <div className="mt-3">
                  <Checklist items={t("hurricanePage.secure")} testId="secure-home-list" />
                </div>
                <h3 className="mt-6 flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-[#0F4C5C]">
                  <ShieldCheck className="h-4 w-4" aria-hidden="true" /> {t("hurricanePage.kitTitle")}
                </h3>
                <div className="mt-3">
                  <Checklist items={t("hurricanePage.kit")} testId="build-kit-list" />
                </div>
              </article>
            </Reveal>
            <Reveal delay={1}>
              <article className="flex h-full flex-col border-t-4 border-[#D97706] bg-[#F8FAFC] p-7" data-testid="during-storm-card">
                <h2 className="font-heading text-xl font-bold text-slate-900">{t("hurricanePage.duringTitle")}</h2>
                <h3 className="mt-5 text-sm font-bold uppercase tracking-wider text-[#0F4C5C]">{t("hurricanePage.stayingTitle")}</h3>
                <div className="mt-3">
                  <Checklist items={t("hurricanePage.staying")} testId="staying-home-list" />
                </div>
                <h3 className="mt-6 text-sm font-bold uppercase tracking-wider text-[#0F4C5C]">{t("hurricanePage.evacTitle")}</h3>
                <div className="mt-3">
                  <Checklist items={t("hurricanePage.evacuating")} testId="evacuating-list" />
                </div>
              </article>
            </Reveal>
            <Reveal delay={2}>
              <article className="flex h-full flex-col border-t-4 border-[#1A365D] bg-[#F8FAFC] p-7" data-testid="after-storm-card">
                <h2 className="font-heading text-xl font-bold text-slate-900">{t("hurricanePage.afterTitle")}</h2>
                <h3 className="mt-5 text-sm font-bold uppercase tracking-wider text-[#0F4C5C]">{t("hurricanePage.safetyTitle")}</h3>
                <div className="mt-3">
                  <Checklist items={t("hurricanePage.after")} testId="after-storm-list" />
                </div>
                <div className="mt-auto pt-6">
                  <p className="border border-[#0F4C5C]/15 bg-white p-4 text-sm leading-relaxed text-slate-600">
                    {t("hurricanePage.calloutText")}
                  </p>
                </div>
              </article>
            </Reveal>
          </div>

          <Reveal>
            <div className="mt-12 flex flex-col items-center justify-between gap-6 bg-[#0F4C5C] p-8 text-white sm:flex-row" data-testid="hurricane-contact-band">
              <div>
                <h2 className="font-heading text-2xl font-bold">{t("hurricanePage.bandTitle")}</h2>
                <p className="mt-2 text-sm text-slate-300">
                  {t("hurricanePage.bandSub")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <a href={SITE.phone1Href} className="inline-flex items-center gap-2 bg-[#D97706] px-6 py-3 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[#B45309]" data-testid="hurricane-call-1">
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone1}
                </a>
                <a href={SITE.phone2Href} className="inline-flex items-center gap-2 border border-white/40 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/10" data-testid="hurricane-call-2">
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phone2}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
